import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Input, Spinner } from "reactstrap";
import SimpleBar from "simplebar-react";

import { formatClientPhoneWithDdi } from "../../helpers/clientPhone";
import { Client, listClients } from "../../services/clientService";

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const RESULTS_PER_PAGE = 8;

const SearchOption = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedTerm.length < MIN_SEARCH_LENGTH) {
      setClients([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    listClients({ search: debouncedTerm, per_page: RESULTS_PER_PAGE })
      .then((response) => {
        if (!cancelled) {
          setClients(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setClients([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedTerm]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const clearSearch = () => {
    setSearchTerm("");
    setDebouncedTerm("");
    setClients([]);
    closeDropdown();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.body.addEventListener("click", handleClickOutside);

    return () => {
      document.body.removeEventListener("click", handleClickOutside);
    };
  }, [closeDropdown]);

  const navigateToClientList = (term: string) => {
    navigate(term ? `/clients?search=${encodeURIComponent(term)}` : "/clients");
    clearSearch();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setIsOpen(value.trim().length > 0);
  };

  const handleFocus = () => {
    if (searchTerm.trim().length > 0) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    navigateToClientList(searchTerm.trim());
  };

  const handleClientClick = (clientId: number) => {
    navigate(`/clients/${clientId}`);
    clearSearch();
  };

  const trimmedTerm = searchTerm.trim();
  const showDropdown = isOpen && trimmedTerm.length > 0;
  const showMinChars =
    trimmedTerm.length > 0 && debouncedTerm.length < MIN_SEARCH_LENGTH && !loading;
  const showNoResults =
    !loading && debouncedTerm.length >= MIN_SEARCH_LENGTH && clients.length === 0;

  return (
    <div className="app-search d-none d-md-block" ref={containerRef}>
      <form className="position-relative" onSubmit={(event) => event.preventDefault()}>
        <Input
          type="text"
          className="form-control"
          placeholder={t("header.search.placeholder")}
          id="search-options"
          value={searchTerm}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onFocus={handleFocus}
          autoComplete="off"
        />
        <span className="mdi mdi-magnify search-widget-icon" />
        {trimmedTerm.length > 0 ? (
          <span
            className="mdi mdi-close-circle search-widget-icon search-widget-icon-close"
            role="button"
            tabIndex={0}
            aria-label={t("header.search.placeholder")}
            onClick={clearSearch}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                clearSearch();
              }
            }}
          />
        ) : null}
        <div
          className={`dropdown-menu dropdown-menu-lg${showDropdown ? " show" : ""}`}
          id="search-dropdown"
        >
          <SimpleBar style={{ height: "320px" }}>
            {loading ? (
              <div className="dropdown-item text-muted d-flex align-items-center gap-2">
                <Spinner size="sm" />
                {t("header.search.loading")}
              </div>
            ) : null}
            {showMinChars ? (
              <div className="dropdown-item text-muted">{t("header.search.minChars")}</div>
            ) : null}
            {showNoResults ? (
              <div className="dropdown-item text-muted">{t("header.search.noResults")}</div>
            ) : null}
            {!loading
              ? clients.map((client) => (
                  <button
                    key={client.id}
                    type="button"
                    className="dropdown-item notify-item py-2 border-0 bg-transparent w-100 text-start"
                    onClick={() => handleClientClick(client.id)}
                  >
                    <div className="d-flex flex-column">
                      <h6 className="m-0">{client.name}</h6>
                      <span className="fs-11 mb-0 text-muted">
                        {formatClientPhoneWithDdi(client.country_code, client.phone_number)}
                      </span>
                    </div>
                  </button>
                ))
              : null}
          </SimpleBar>
          {trimmedTerm.length >= MIN_SEARCH_LENGTH ? (
            <div className="text-center pt-3 pb-1">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => navigateToClientList(trimmedTerm)}
              >
                {t("header.search.viewAll")}
                <i className="ri-arrow-right-line ms-1" />
              </button>
            </div>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default SearchOption;
